import React, { useEffect, useState, useCallback, useMemo } from 'react';

const DISMISS_KEY = 'pwaInstallPromptDismissed';

/**
 * Detect iOS / iPadOS (including iPads that report as Mac desktop UA).
 * All iOS browsers (Safari, Chrome, Edge, Firefox) use WebKit — none get
 * `beforeinstallprompt`; install is Share → Add to Home Screen.
 */
function getIosBrowserInfo() {
  if (typeof navigator === 'undefined') {
    return { isIos: false, browser: null };
  }

  const ua = navigator.userAgent || '';
  const isIphoneIpod = /iPhone|iPod/.test(ua);
  const isIpad =
    /iPad/.test(ua) ||
    // iPadOS 13+ can report as Macintosh + touch
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

  if (!isIphoneIpod && !isIpad) {
    return { isIos: false, browser: null };
  }

  // Order matters: CriOS/EdgiOS/FxiOS also contain "Safari" in the UA string.
  let browser = 'safari';
  if (/CriOS\//.test(ua)) browser = 'chrome';
  else if (/EdgiOS\//.test(ua)) browser = 'edge';
  else if (/FxiOS\//.test(ua)) browser = 'firefox';
  else if (/OPiOS\//.test(ua) || /OPT\//.test(ua)) browser = 'opera';

  return { isIos: true, browser };
}

function iosInstallCopy(browser) {
  switch (browser) {
    case 'chrome':
      return 'On iPhone Chrome: tap Share → Add to Home Screen for quicker access.';
    case 'edge':
      return 'On iPhone Edge: tap Share → Add to Home Screen for quicker access.';
    case 'firefox':
      return 'On iPhone Firefox: tap Share → Add to Home Screen. Safari works best for app mode.';
    case 'opera':
      return 'On iPhone: open in Safari, then Share → Add to Home Screen.';
    default:
      return 'Install this app: tap Share → Add to Home Screen for quicker access and offline support.';
  }
}

/**
 * Chrome/Edge (Android & desktop): shows when the browser fires `beforeinstallprompt`.
 * iOS (Safari / Chrome / Edge / …): instructional banner — no native install API.
 */
function InstallPwaPrompt() {
  const ios = useMemo(() => getIosBrowserInfo(), []);
  const [deferred, setDeferred] = useState(null);
  const [showIosTip, setShowIosTip] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(DISMISS_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(display-mode: standalone)');
    const check = () => {
      setIsStandalone(
        mq.matches ||
          window.navigator.standalone === true ||
          document.referrer.includes('android-app://')
      );
    };
    check();
    mq.addEventListener('change', check);
    return () => mq.removeEventListener('change', check);
  }, []);

  // iOS: show tip after a short delay (avoid flash on load)
  useEffect(() => {
    if (!ios.isIos || isStandalone || dismissed) {
      setShowIosTip(false);
      return undefined;
    }
    const t = window.setTimeout(() => setShowIosTip(true), 1200);
    return () => window.clearTimeout(t);
  }, [ios.isIos, isStandalone, dismissed]);

  // Android / desktop Chromium: native install prompt
  useEffect(() => {
    if (ios.isIos || isStandalone || dismissed) return undefined;

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
    };

    window.addEventListener('beforeinstallprompt', onPrompt);
    return () => window.removeEventListener('beforeinstallprompt', onPrompt);
  }, [ios.isIos, isStandalone, dismissed]);

  const onLater = useCallback(() => {
    setDismissed(true);
    setDeferred(null);
    setShowIosTip(false);
    try {
      localStorage.setItem(DISMISS_KEY, '1');
    } catch {
      /* quota / private mode — still hidden for this session */
    }
  }, []);

  const onInstall = useCallback(async () => {
    if (!deferred) return;
    deferred.prompt();
    await deferred.userChoice.catch(() => {});
    setDeferred(null);
  }, [deferred]);

  if (isStandalone || dismissed) return null;

  // —— iOS instructional banner (Safari / Chrome / Edge / …) ——
  if (ios.isIos) {
    if (!showIosTip) return null;
    return (
      <div
        className="fixed bottom-20 left-3 right-3 z-[60] flex items-center justify-between gap-3 rounded-2xl border border-teal-200 bg-white p-3 shadow-lg dark:border-teal-800 dark:bg-gray-900 sm:left-auto sm:right-4 sm:max-w-md"
        role="status"
      >
        <p className="text-sm text-gray-800 dark:text-gray-100">
          {iosInstallCopy(ios.browser)}
        </p>
        <div className="flex shrink-0 gap-2">
          <button
            type="button"
            onClick={onLater}
            className="rounded-full px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"
          >
            Later
          </button>
          <button
            type="button"
            onClick={onLater}
            className="rounded-full bg-teal-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-teal-700"
          >
            Got it
          </button>
        </div>
      </div>
    );
  }

  // —— Chromium native install ——
  if (!deferred) return null;

  return (
    <div
      className="fixed bottom-20 left-3 right-3 z-[60] flex items-center justify-between gap-3 rounded-2xl border border-teal-200 bg-white p-3 shadow-lg dark:border-teal-800 dark:bg-gray-900 sm:left-auto sm:right-4 sm:max-w-md"
      role="status"
    >
      <p className="text-sm text-gray-800 dark:text-gray-100">
        Install this app for quicker access and offline support.
      </p>
      <div className="flex shrink-0 gap-2">
        <button
          type="button"
          onClick={onLater}
          className="rounded-full px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"
        >
          Later
        </button>
        <button
          type="button"
          onClick={onInstall}
          className="rounded-full bg-teal-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-teal-700"
        >
          Install
        </button>
      </div>
    </div>
  );
}

export default InstallPwaPrompt;
